import type {
  PercentileFactorRange,
  ProductMarketEstimate,
  ProjectMarketEstimates,
} from '../models/project.model';
import type { ProductID } from '../models/product.model';
import type { ProductCategoryId } from '../models/element-categories.model';
import {
  DEFAULT_LIMITS,
  getCategoryDefaults,
  getMarketEstimateData,
  getProductLimits,
  type MarketEstimateLimits,
} from './product-market-estimates.data';
import { getElementProductCategory } from './element-category.helpers';
import { getObjectHash } from './object.helpers';

/**
 * Convert percentile (0-100) to adjustment factor.
 * Extrapolates linearly outside the data range.
 *
 * @param percentile - Percentile value (0-100 scale)
 * @param range - The {@link PercentileFactorRange} defining the mapping
 * @returns Adjustment factor (e.g., -0.3 for -30% adjustment)
 *
 * @example
 * // P5-P95 range with -30% to +30% factors
 * const range = { percentileRange: { min: 5, max: 95 }, minFactor: -0.3, maxFactor: 0.3 };
 * percentileToFactor(50, range); // 0 (midpoint)
 * percentileToFactor(5, range);  // -0.3
 * percentileToFactor(95, range); // 0.3
 * percentileToFactor(0, range);  // -0.333... (extrapolated)
 */
export const percentileToFactor = (
  percentile: number,
  range: PercentileFactorRange,
): number => {
  const { percentileRange, minFactor, maxFactor } = range;
  const { min: pMin, max: pMax } = percentileRange;
  const pSpan = pMax - pMin;
  if (pSpan === 0) return (minFactor + maxFactor) / 2;
  return minFactor + ((percentile - pMin) / pSpan) * (maxFactor - minFactor);
};

/**
 * Convert adjustment factor to percentile (0-100).
 * Extrapolates linearly outside the data range.
 *
 * @param factor - Adjustment factor (e.g., -0.3 for -30% adjustment)
 * @param range - The {@link PercentileFactorRange} defining the mapping
 * @returns Percentile value (0-100 scale)
 *
 * @example
 * const range = { percentileRange: { min: 5, max: 95 }, minFactor: -0.3, maxFactor: 0.3 };
 * factorToPercentile(0, range);    // 50 (midpoint)
 * factorToPercentile(-0.3, range); // 5
 * factorToPercentile(0.3, range);  // 95
 */
export const factorToPercentile = (
  factor: number,
  range: PercentileFactorRange,
): number => {
  const { percentileRange, minFactor, maxFactor } = range;
  const { min: pMin, max: pMax } = percentileRange;
  const factorSpan = maxFactor - minFactor;
  if (factorSpan === 0) return (pMin + pMax) / 2;
  return pMin + ((factor - minFactor) / factorSpan) * (pMax - pMin);
};

/**
 * Get estimates for a product, falling back to category defaults then global defaults.
 *
 * @param marketEstimates - Project-level market estimates snapshot
 * @param productId - Generic product ID to look up
 * @returns Product-specific estimates, category defaults, or global defaults. Undefined if no estimates available.
 */
export const getProductEstimates = (
  marketEstimates: ProjectMarketEstimates | undefined,
  productId: ProductID,
): ProductMarketEstimate | undefined => {
  if (!marketEstimates) return undefined;
  if (marketEstimates.products[productId])
    return marketEstimates.products[productId];
  const category = getElementProductCategory(productId);
  return marketEstimates.categoryDefaults[category] ?? marketEstimates.defaults;
};

// === Market Estimates Snapshot Creation ===

/** Standard P5-P95 percentile range for market estimates */
const STANDARD_PERCENTILE_RANGE = { min: 5, max: 95 };

/**
 * Convert limits format to PercentileFactorRange format.
 * The limits represent P5-P95, so minFactor is at P5 and maxFactor is at P95.
 */
const limitsToPercentileFactorRange = (
  limits: MarketEstimateLimits['carbon'],
): PercentileFactorRange => ({
  percentileRange: STANDARD_PERCENTILE_RANGE,
  minFactor: limits.min,
  maxFactor: limits.max,
});

/**
 * Convert limits to ProductMarketEstimate format.
 */
const limitsToProductMarketEstimate = (
  limits: MarketEstimateLimits,
): ProductMarketEstimate => {
  const carbonSpan = limits.carbon.max - limits.carbon.min;
  const costSpan = limits.cost.min - limits.cost.max;
  const carbonToCost = carbonSpan === 0 ? 0 : costSpan / carbonSpan;
  const costToCarbon = carbonToCost === 0 ? 0 : 1 / carbonToCost;

  return {
    carbon: limitsToPercentileFactorRange(limits.carbon),
    cost: limitsToPercentileFactorRange(limits.cost),
    coefficients: { carbonToCost, costToCarbon },
  };
};

/**
 * Convert EstimateDefaults to MarketEstimateLimits format.
 */
const estimateDefaultsToLimits = (defaults: {
  co2Diff: number;
  costK: number;
}): MarketEstimateLimits => ({
  carbon: { min: -defaults.co2Diff, max: defaults.co2Diff },
  cost: {
    min: -(defaults.co2Diff * defaults.costK),
    max: defaults.co2Diff * defaults.costK,
  },
});

/**
 * Create a ProjectMarketEstimates snapshot from global market estimate data.
 * This is used for lazy initialization when the first override is added to a project.
 *
 * @returns A complete ProjectMarketEstimates snapshot
 */
export const createMarketEstimatesSnapshot = (): ProjectMarketEstimates => {
  const defaults = limitsToProductMarketEstimate(DEFAULT_LIMITS);

  // Build category defaults
  const categoryDefaultsConfig = getCategoryDefaults();
  const categoryDefaults = Object.fromEntries(
    Object.entries(categoryDefaultsConfig).map(([categoryId, config]) => [
      categoryId,
      limitsToProductMarketEstimate(estimateDefaultsToLimits(config)),
    ]),
  ) as Record<ProductCategoryId, ProductMarketEstimate>;

  // Create per-product estimates for all valid products
  const products: Record<ProductID, ProductMarketEstimate> = {};
  const marketEstimateData = getMarketEstimateData();

  for (const [id, result] of Object.entries(marketEstimateData)) {
    if (result?.valid === true) {
      const limits = getProductLimits(id);
      products[id] = limitsToProductMarketEstimate(limits);
    }
  }

  const data = { defaults, categoryDefaults, products };
  const hash = getObjectHash(data);

  return { ...data, snapshotHash: hash };
};
